import * as THREE from 'three';
import geometryUpdates from '@/mixins/geometryUpdates';

const TWEEN = require('@tweenjs/tween.js');

export default {
  mixins: [
    geometryUpdates
  ],
  computed: {
    pdm() {
      return this.$store.state.utils.pdm;
    },
    default_textures() {
      return this.$store.state.sceneSetup.default_textures;
    },
    queryParams() {
      return this.$store.state.utils.queryParams;
    },
    isMobile() {
      return this.$store.getters["utils/returnScreenSize"].includes("mobile");
    }
  },
  methods: {
    /**
     * @function setupRenderer
     * Sets up a THREE  webgl renderer with data from the PDM to render the scene into the canvas
     * Appends the rendered scene on the dom element "scene".
     */
    setupRenderer() {
      const { alpha, antialias, high_resolution, setClearColor } = this.pdm.default_display.scene_defaults.renderer;
      const { precision, shadowMap, shadowMapSoft, shadowMapType } = high_resolution;
      const preserveDrawingBuffer = this.$store.getters["utils/getUrlParameterByName"]("preserveDrawingBuffer") === "true" || this.queryParams.slim === "true";
      const DPR = this.queryParams.slim ? 1 : window.devicePixelRatio || 1;
      const { innerWidth: WW, innerHeight: HH } = window;

      this.$model3D.renderer = new THREE.WebGLRenderer({ alpha, antialias, precision, preserveDrawingBuffer, });
      this.$model3D.renderer.setSize(WW, HH);
      this.$model3D.renderer.setPixelRatio(DPR);
      this.$model3D.renderer.setViewport(0, 0, WW * DPR, HH * DPR);
      this.$model3D.renderer.shadowMap.enabled = shadowMap;
      this.$model3D.renderer.shadowMapSoft = shadowMapSoft;
      this.$model3D.renderer.shadowMap.type = THREE[shadowMapType];

      document.getElementById('scene').appendChild(this.$model3D.renderer.domElement);
    },
    /**
     * @function loadDefaultAssets
     * @return {Array} Promises - Array of promises, each promise represent a call for one texture.
     * Loads the initial textures set in the PDM for a model for all components
     * and saves it into the default_textures object
     * This object is use futher down the flow to apply the textures to the 3D model.
     */
    loadDefaultAssets() {
      const promises = [];
      const components = Object.keys(this.pdm.components);
      components.forEach(componentName => {
        const componentObj = this.pdm.components[componentName];
        promises.push(this.loadDefaultGeometries({componentObj, componentName}))
      });
      return new Promise(resolve => {
        Promise.all(promises).then(resolve);
      });
    },
    /**
     * @function saveTexturePromise
     * @param {Array} promises - Array of promises, each promise represent a call for one texture.
     * @param {Object} componentObj - Object with the component data tha contains the textures to load
     * @param {String} componentName - Name of component that needs textures loaded
     * Saves the texture load promises into the promises array and saves the default textures
     *  into the the default_textures object once they are loaded
     */
    saveTexturePromise(componentObj, componentName) {
      const promises = [];
      if (componentObj.mesh_material && componentObj.mesh_material.textures) {
        const textures = Object.keys(componentObj.mesh_material.textures);
        textures.forEach(i => {
          const texture = componentObj.mesh_material.textures[i];
          const newPromise = new Promise(resolve => {
            this.$model3D.loadTexture({
              image_url: `${this.$store.state.endPoints.customizer.s3SwatchesBucket}assets${texture.image_url}`,
              options: texture.options,
            })
            .then(textureObj => {
              if (this.default_textures[componentName] && this.default_textures[componentName][texture.map_type]) {
                this.$model3D.sceneHelpers.dispose(this.default_textures[componentName][texture.map_type]);
              }
              this.$store.commit("sceneSetup/SAVE_DEFAULT_TEXTURES", {componentName, map_type: i, textureObj});
              resolve();
            })
          });
          promises.push(newPromise);
        });
        return Promise.all(promises)
      }
      return Promise.resolve();
    },
    /**
     * @function responsiveModelDefaults
     * Does the intial positioning, scaling, zoom, etc of the
     * 3D Model based on the rules defined in the product data model
     */
    responsiveModelDefaults() {
      const { responsive_defaults } = this.pdm.default_display.scene_defaults;
      let matchedDefaults = {};
      for (let item of responsive_defaults) {
        let query = "only screen";
        query = `${query} and (min-width: ${item.breakpoint}px)`;
        if (typeof(item.orientation) !== 'undefined') {
          query = `${query} and (orientation: ${item.orientation})`;
        }
        if(window.matchMedia(query).matches) {
          matchedDefaults = item;
        }
      }
      const canvasSize = this.$model3D.renderer.getSize();
      if(this.isMobile && 
        canvasSize.x !== canvasSize.y && 
        matchedDefaults.minScaleMobile
      ) {
        const ratio = canvasSize.y/canvasSize.x;
        const min = matchedDefaults.minScaleMobile;
        const max = matchedDefaults.maxScaleMobile;
        const scale = max - (ratio - 1) * (max - min);
        this.$model3D.rootObj.scale.set(
          scale,
          scale,
          scale
        );
      } else {
        this.$model3D.rootObj.scale.set(
          matchedDefaults.scale.x,
          matchedDefaults.scale.y,
          matchedDefaults.scale.z
        );
      }
      this.$model3D.rootObj.position.set(
        matchedDefaults.position.x,
        matchedDefaults.position.y,
        matchedDefaults.position.z
      );
      if (typeof(matchedDefaults.target) !== 'undefined') {
        this.controls.target.set(
          matchedDefaults.target.x,
          matchedDefaults.target.y,
          matchedDefaults.target.z
        );
      }
      this.controls.minDistance = matchedDefaults.minDistance;
      this.controls.maxDistance = matchedDefaults.maxDistance;
    },

    /**
     * @function rotateModel
     * Rotate the model using Tween library.
     */
    rotateModel() {
      this.showRotateMsg = true;
      const tween = new TWEEN.default.Tween(this.$model3D.rootObj.rotation).to({
        y: Math.PI * 2
      }, 2500).delay(400).onComplete(() => {
        setTimeout(()=> {
          this.showRotateMsg = false;
          this.rotateStart();
          this.controls.enableRotate = true;
          this.$eventBus.$emit("component-selected", this.activeComponent);
        }, 1300);
      }).easing(TWEEN.default.Easing.Back.InOut).start();
    },

    /**
     * @function rotateStart
     * Starts the timer. Uses prop autoRotateTimeout for duration.
     */
    rotateStart() {
      if (!this.$model3D.rootObj || typeof this.$model3D.rootObj === "undefined") {
        return;
      }
      if (!this.autoRotateEnabled) return;

      if (this.autoRotate.timer) clearTimeout(this.autoRotate.timer);

      this.autoRotate.timer = setTimeout(() => {
        this.controls.autoRotate = true;
      }, this.autoRotateTimeout);
    },

    /**
     * @function rotateStop
     * @param {Boolean} stopPermanently
     * Check for optional argument stopPermanently to permanently stop autoRotate.
     */
    rotateStop(stopPermanently) {
      if (this.controls.autoRotate) {
        this.controls.autoRotate = false;
      }
      if (!stopPermanently) {
        this.rotateStart();
      }
    },
    async startScreenshotForService() {
      const {size, view, images, recipe} = this.queryParams
      if(images){
        for (const img of JSON.parse(images)) {
          await this.positionScene({view: img.view, size: img.size})
          setTimeout(()=>{
            this.takeScreenShot({size: img.size, view: img.view, recipeId: recipe});
          }, 300)
        }
      }

      if(view && size) {
       await this.positionScene({view, size});
        setTimeout(()=>{
          this.takeScreenShot({size, view, recipeId: recipe});
        },300)
      }
    },
    positionScene({view, size}){
      return new Promise(resolve => {
        const pdmRendition = this.pdm.product_images?.renditions?.[size];
        const { width,height,background_color } = pdmRendition;
        const pdmView = this.pdm.product_images?.snapshot_views?.[view];
        // model
        this.$model3D.modelObj.scale.set(pdmView.scale.x,pdmView.scale.y,pdmView.scale.z);
        pdmView.scale.y < 0 && this.handleLeftShoe(pdmView);

        // camera
        this.$model3D.camera.aspect = width/height;
        this.$model3D.camera.position.set(pdmView.camera_position.x,pdmView.camera_position.y,pdmView.camera_position.z);
        this.$model3D.camera.zoom = pdmView.zoom
        this.$model3D.camera.updateProjectionMatrix();

        //renderer
        this.$model3D.renderer.setSize(width, height, true);
        this.$model3D.renderer.setClearColor(Number(background_color), 1);
        resolve();
      });
    },

    handleLeftShoe(pdmView) {
      // Flip Component
      if(pdmView.components) {
        const components = pdmView.components && Object.keys(pdmView.components);
        components?.forEach((componentName) => {
          const pdmComponent = pdmView.components[componentName];
          const pos = pdmComponent.position;
          const rot = pdmComponent.rotation;
          const component = this.model3d.getObjectByName(componentName);
          pdmComponent.flipHorizontal && component.scale.set(component.scale.x,-component.scale.y,component.scale.z);
          pos && component.position.set(pos.x,pos.y,pos.z);
          rot && component.rotation.set(rot.x,rot.y,rot.z);
        })
      }
    },

    takeScreenShot({size, view, recipeId, showSnapShot = false}){
      this.$model3D.renderer.domElement.toBlob((blob) => {
        this.$store.dispatch("utils/GET_PRODUCT_IMAGES_FROM_SCENE", { blob, recipeId, size, view}).then((data) => {
          this.$eventBus.$emit("snapshot-complete", data);
        });
      }, 'image/jpg'); 
    }
  }
};
